import { MasterTranslation } from '@/constants';
import { useGlobalState } from '@/hooks';
import type { ICustomerReview } from '@/interfaces';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowIcon } from '../InsightsIcons';
import { InsightsContainer, ReviewsSectionTitle } from '../TrackingInsights.styles';
import {
  ReviewAuthor,
  ReviewCard,
  ReviewNav,
  ReviewNavButton,
  ReviewQuote,
  ReviewTrack,
  ReviewsSection,
} from './CustomerReviews.styles';

/** Sub-pixel scroll offsets (zoom, fractional DPR) must not read as "not quite at the edge". */
const EDGE_TOLERANCE = 2;

export interface CustomerReviewsProps {
  reviews: ICustomerReview[];
  tinted?: boolean;
}

/**
 * Reader reviews for one tracking page (Figma 20561:27767 desktop, 20561:27980 mobile).
 *
 * A horizontal row of quote cards that runs off the right edge of the page, paged a screenful at a
 * time by a pair of arrow buttons underneath. The Contentful `customerReviews` field carries only
 * the quote, so the byline is the same translated "Verified User" label on every card, as drawn.
 *
 * Every card stays in the markup: scrolling is presentation only, so crawlers read every review.
 */
const CustomerReviews: React.FC<CustomerReviewsProps> = ({ reviews, tinted }) => {
  const { t } = useGlobalState();
  const trackRef = useRef<HTMLDivElement>(null);
  // Server render and first paint assume a scrollable row; the measurement below corrects it.
  const [edges, setEdges] = useState({ atStart: true, atEnd: reviews.length <= 1 });

  const updateEdges = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;

    const { scrollLeft, scrollWidth, clientWidth } = track;
    setEdges({
      atStart: scrollLeft <= EDGE_TOLERANCE,
      atEnd: scrollLeft + clientWidth >= scrollWidth - EDGE_TOLERANCE,
    });
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measure when the list changes length
  useEffect(() => {
    updateEdges();
    window.addEventListener('resize', updateEdges);
    return () => window.removeEventListener('resize', updateEdges);
  }, [updateEdges, reviews.length]);

  /**
   * Pages the row by as many cards as the content column shows in full, landing the first card of
   * the new page on the column — so when a single card is left out of view, one press reaches the
   * end. The browser clamps the last page to the end of the row.
   *
   * Positions are read from the cards themselves rather than computed from a fixed card width: the
   * card shrinks on narrow phones.
   */
  const scrollByPage = (direction: 1 | -1) => {
    const track = trackRef.current;
    const column = track?.parentElement;
    if (!track || !column) return;

    const cards = Array.from(track.children) as HTMLElement[];
    if (!cards.length) return;

    const origin = cards[0].offsetLeft;
    const positions = cards.map((card) => card.offsetLeft - origin);
    const cardStep = cards.length > 1 ? positions[1] : cards[0].offsetWidth;

    const { paddingLeft, paddingRight } = window.getComputedStyle(column);
    const columnWidth =
      column.clientWidth - (Number.parseFloat(paddingLeft) || 0) - (Number.parseFloat(paddingRight) || 0);
    // One gap is added back because the last card on a page needs none after it.
    const gap = cardStep - cards[0].offsetWidth;
    const cardsPerPage = Math.max(1, Math.floor((columnWidth + gap) / cardStep));

    // The card nearest the column edge, so a row resting a few px off a card still pages from it.
    const current = positions.reduce(
      (nearest, position, index) =>
        Math.abs(position - track.scrollLeft) < Math.abs(positions[nearest] - track.scrollLeft) ? index : nearest,
      0,
    );
    const targetIndex = Math.min(Math.max(current + direction * cardsPerPage, 0), positions.length - 1);

    // `scrollTo` on elements is missing from some test DOMs; smoothness comes from CSS.
    track.scrollTo?.({ left: positions[targetIndex] });
  };

  if (!reviews.length) return null;

  // Nothing to page through when every card fits: the arrows would both be dead.
  const hasNav = !(edges.atStart && edges.atEnd);

  return (
    <ReviewsSection $tinted={tinted}>
      <InsightsContainer>
        <ReviewsSectionTitle type='h2'>{t(MasterTranslation.CustomerReviewsTitle)}</ReviewsSectionTitle>

        <ReviewTrack ref={trackRef} onScroll={updateEdges}>
          {reviews.map((review, index) => (
            <ReviewCard key={`${index}-${review.quote.slice(0, 24)}`} $onTint={tinted}>
              <ReviewQuote>{review.quote}</ReviewQuote>
              <ReviewAuthor>{t(MasterTranslation.CustomerReviewsAuthor)}</ReviewAuthor>
            </ReviewCard>
          ))}
        </ReviewTrack>

        {hasNav && (
          <ReviewNav>
            <ReviewNavButton
              type='button'
              aria-label={t(MasterTranslation.CustomerReviewsPrevious)}
              disabled={edges.atStart}
              onClick={() => scrollByPage(-1)}
            >
              <ArrowIcon direction='left' />
            </ReviewNavButton>
            <ReviewNavButton
              type='button'
              aria-label={t(MasterTranslation.CustomerReviewsNext)}
              disabled={edges.atEnd}
              onClick={() => scrollByPage(1)}
            >
              <ArrowIcon direction='right' />
            </ReviewNavButton>
          </ReviewNav>
        )}
      </InsightsContainer>
    </ReviewsSection>
  );
};

export default CustomerReviews;
